import { FC, MouseEvent, useCallback, useMemo } from 'react';

type Direction = 'none' | 'forward' | 'backward';

export type LinkButtonProps = {
  /**
   * Label of the link button
   */
  label?: string | JSX.Element;
  /**
   * Direction of the arrow icon
   */
  direction?: Direction;
  /**
   * Click action of link button
   */
  onClick?(): void;
  /**
   * Flag to disable link button
   */
  disabled?: boolean;
};

const BASE_LINKBUTTON_CLASSES = 'font-sans uppercase font-medium text-xs';
const DEFAULT_LINKBUTTON_CLASSES = `${BASE_LINKBUTTON_CLASSES} text-blue-200 hover:text-blue-100`;
const DISABLED_LINKBUTTON_CLASSES = `${BASE_LINKBUTTON_CLASSES} text-gray-400 hover:text-gray-400 cursor-default focus:outline-none`;

export const LinkButton: FC<LinkButtonProps> = ({
  label = 'Link Button',
  direction = 'none',
  onClick,
  disabled,
}: LinkButtonProps) => {
  const className = useMemo(
    () => (disabled ? DISABLED_LINKBUTTON_CLASSES : DEFAULT_LINKBUTTON_CLASSES),
    [disabled],
  );

  const onClickHandler = useCallback(
    () =>
      disabled || !onClick
        ? (event: MouseEvent) => {
            event.preventDefault();
            return false;
          }
        : onClick(),
    [disabled, onClick],
  );

  return (
    <button className={className} onClick={onClickHandler} data-testid="linkButton">
      {direction === 'backward' && '< '}
      {label}
      {direction === 'forward' && ' >'}
    </button>
  );
};
